import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ParticipationToday } from "@tih/shared";
import { fetchParticipationToday } from "../../api/client";
import { useReveal } from "../../hooks/useReveal";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { CopyText } from "../CopyText";
import { getAtPath } from "../../lib/siteCopyPaths";
import { EventProximityBanner } from "./EventProximityBanner";
import { ParticipationSpotlight } from "./ParticipationSpotlight";
import { ParticipationWall } from "./ParticipationWall";
import { EventPrepChecklist } from "./EventPrepChecklist";

const POLL_MS = 30_000;

export function HomeParticipationHub() {
  const ref = useReveal<HTMLElement>();
  const { copy } = useSiteCopy();
  const [data, setData] = useState<ParticipationToday | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchParticipationToday()
      .then(setData)
      .catch((e) => setError(String(e?.message ?? e)));
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  if (error) return null;
  if (!data) {
    return (
      <section className="section participation-hub" ref={ref}>
        <div style={{ opacity: 0.5 }}>Loading…</div>
      </section>
    );
  }

  const showPrep =
    data.featuredEvent &&
    data.daysOut != null &&
    data.daysOut <= 14 &&
    data.daysOut >= 1 &&
    (data.proximity === "approach" || data.proximity === "countdown" || data.proximity === "eve");

  return (
    <section className="section participation-hub" ref={ref}>
      <div className="sec-eyebrow center">
        <em>
          <CopyText path="home.participation.eyebrow" as="span" />
        </em>
      </div>
      <h2 className="sec-title sec-title-sm center">
        <CopyText path="home.participation.title" as="span" />
      </h2>
      <p className="sec-lede center" style={{ maxWidth: 640, margin: "0 auto 40px", opacity: 0.82 }}>
        <CopyText path="home.participation.lede" as="span" />
      </p>

      <EventProximityBanner
        proximity={data.proximity}
        featured={data.featuredEvent}
        daysOut={data.daysOut}
      />

      <ParticipationSpotlight data={data} onUpdate={load} />

      {showPrep && data.featuredEvent && (
        <div className="participation-hub-prep">
          <EventPrepChecklist eventId={data.featuredEvent.id} items={data.prepChecklist} />
        </div>
      )}

      <ParticipationWall items={data.wall} spotlightSlug={data.spotlightSlug} />

      <p className="participation-explore center">
        <Link to="/participate">
          {String(getAtPath(copy, "home.participation.exploreAll") ?? "Explore all ways to participate")}{" "}
          <span className="arr">→</span>
        </Link>
      </p>
    </section>
  );
}
